/**
 * arbi completion install    — append shell completion function to rc file
 * arbi completion uninstall  — remove completion block from rc file
 * arbi completion refresh    — authenticate + fetch workspaces + update cache
 */

import { existsSync, readFileSync, writeFileSync, appendFileSync } from 'node:fs'
import { homedir } from 'node:os'
import { join } from 'node:path'
import { Command } from 'commander'
import { workspaces } from '@arbidocs/sdk'
import { runAction, resolveAuth } from '../helpers.js'
import { success, dim } from '../output.js'
import { updateCompletionCache } from '../completion-cache.js'

const MARKER_START = '# arbi-cli completion start'
const MARKER_END = '# arbi-cli completion end'

const BASH_COMPLETION = `
${MARKER_START}
_arbi_completions() {
  local cur="\${COMP_WORDS[COMP_CWORD]}"
  local candidates
  candidates=$(arbi --get-completions "\${COMP_LINE}" 2>/dev/null)
  COMPREPLY=($(compgen -W "$candidates" -- "$cur"))
}
complete -o default -F _arbi_completions arbi
${MARKER_END}`

const ZSH_COMPLETION = `
${MARKER_START}
_arbi_completions() {
  local candidates
  candidates=(\${(f)"$(arbi --get-completions "\${words[*]}" 2>/dev/null)"})
  compadd -a candidates
}
compdef _arbi_completions arbi
${MARKER_END}`

function getShellRcPath(): string {
  const shell = process.env.SHELL || ''
  if (shell.includes('zsh')) return join(homedir(), '.zshrc')
  return join(homedir(), '.bashrc')
}

function isZsh(): boolean {
  return (process.env.SHELL || '').includes('zsh')
}

function isCompletionInstalled(rcPath: string): boolean {
  if (!existsSync(rcPath)) return false
  const content = readFileSync(rcPath, 'utf-8')
  return content.includes(MARKER_START)
}

function removeCompletionBlock(content: string): string {
  const startIdx = content.indexOf(MARKER_START)
  const endIdx = content.indexOf(MARKER_END)
  if (startIdx === -1 || endIdx === -1) return content

  // Remove from the newline before the start marker to end of the end marker line
  const before = content.substring(0, startIdx).replace(/\n+$/, '')
  const after = content.substring(endIdx + MARKER_END.length)
  return before + after
}

export function registerCompletionCommand(program: Command): void {
  const completion = program
    .command('completion')
    .description('Shell tab completion: install, uninstall, refresh')

  completion
    .command('install')
    .description('Install shell tab completion (auto-detects bash or zsh — no shell arg needed)')
    .action(() => {
      const rcPath = getShellRcPath()

      if (isCompletionInstalled(rcPath)) {
        success(`Completion already installed in ${rcPath}`)
        dim("If it's not working, run: source " + rcPath)
        return
      }

      const snippet = isZsh() ? ZSH_COMPLETION : BASH_COMPLETION
      appendFileSync(rcPath, snippet + '\n')
      success(`Installed tab completion in ${rcPath}`)
      dim('')
      dim(`Run: source ${rcPath}`)
      dim('Then try: arbi <TAB>')
    })

  completion
    .command('uninstall')
    .description('Remove shell tab completion')
    .action(() => {
      const rcPath = getShellRcPath()

      if (!isCompletionInstalled(rcPath)) {
        dim('Completion is not installed.')
        return
      }

      const content = readFileSync(rcPath, 'utf-8')
      const cleaned = removeCompletionBlock(content)
      writeFileSync(rcPath, cleaned)
      success(`Removed tab completion from ${rcPath}`)
      dim(`Run: source ${rcPath}`)
    })

  completion
    .command('refresh')
    .description('Refresh cached workspace IDs for tab completion')
    .action(
      runAction(async () => {
        const { arbi } = await resolveAuth()
        const data = await workspaces.listWorkspaces(arbi)
        updateCompletionCache(data)
        success(`Cached ${data.length} workspace(s) for tab completion.`)
      })
    )
}
